// apps/app/src/components/ui/DataCombobox.tsx

import * as Combobox from '@/components/ui/combobox'
import { createListCollection } from '@ark-ui/solid'
import { useQuery } from '@tanstack/solid-query'
import { For, createMemo, createSignal } from 'solid-js'
import { css } from 'styled-system/css'

export interface DataItem {
  label: string
  value: string
}

interface DataComboboxProps {
  label: string
  placeholder?: string
  value?: string | null
  onValueChange: (value: string | null) => void
  queryKey: string
  fetchFn: (search: string) => Promise<DataItem[]>
  // DRY improvement: Optional prop if the parent already has the object
  initialItem?: DataItem | null
  allowCustomValue?: boolean
  error?: string
}

export const DataCombobox = (props: DataComboboxProps) => {
  const [searchTerm, setSearchTerm] = createSignal('')

  // The main search query - disabled if search is empty
  const query = useQuery(() => ({
    queryKey: [props.queryKey, searchTerm()],
    queryFn: () => props.fetchFn(searchTerm()),
    enabled: searchTerm().trim().length > 0, // Don't fetch on empty input
    placeholderData: (prev) => prev,
  }))

  const collection = createMemo(() => {
    let items: DataItem[] = query.data ?? []
    const currentSearch = searchTerm().trim()

    // Ensure the current value's label is always in the list (Hydration)
    // ADDED: Check that props.initialItem.value !== ''
    if (
      props.initialItem &&
      props.initialItem.value !== '' &&
      items.every(i => i.value !== props.initialItem?.value)
    ) {
      items = [props.initialItem, ...items]
    }

    if (props.allowCustomValue && currentSearch !== '') {
      const isExactMatch = items.some(
        (item) => (item.label || item.value).toLowerCase() === currentSearch.toLowerCase()
      )
      if (!isExactMatch) {
        items = [{ label: `Create "${currentSearch}"`, value: currentSearch }, ...items]
      }
    }

    return createListCollection({ items })
  })

  return (
    <Combobox.Root
      collection={collection()}
      value={props.value ? [props.value] : []}
      onValueChange={(details) => props.onValueChange(details.value[0] || null)}
      onInputValueChange={(e) => setSearchTerm(e.inputValue)}
      allowCustomValue={props.allowCustomValue ?? false}
      openOnClick={false}
    >
      <Combobox.Label>{props.label}</Combobox.Label>
      <Combobox.Control>
        <Combobox.Input
          placeholder={props.placeholder}
          onBlur={() => {
            if (props.allowCustomValue && searchTerm().trim() !== '' && props.value !== searchTerm().trim()) {
              props.onValueChange(searchTerm().trim())
            }
          }}
        />
        <Combobox.IndicatorGroup>
          <Combobox.ClearTrigger />
          {/* <Combobox.Trigger /> */}
        </Combobox.IndicatorGroup>
      </Combobox.Control>

      <Combobox.Positioner class={css({ zIndex: '1500!' })}>
        <Combobox.Content>
          {query.isFetching && !query.data && (
            <Combobox.Item item={{ label: 'Loading...', value: 'loading' }}>
              <Combobox.ItemText>Loading...</Combobox.ItemText>
            </Combobox.Item>
          )}
          <Combobox.Empty>
            {props.allowCustomValue && searchTerm() ? `Press Enter to use "${searchTerm()}"` : 'No results found'}
          </Combobox.Empty>
          <For each={collection().items}>
            {(item) => (
              <Combobox.Item item={item}>
                <Combobox.ItemText>{item.label}</Combobox.ItemText>
                <Combobox.ItemIndicator />
              </Combobox.Item>
            )}
          </For>
        </Combobox.Content>
      </Combobox.Positioner>
      {props.error && <p class={css({ color: 'red.500', fontSize: 'sm', mt: '1' })}>{props.error}</p>}
    </Combobox.Root>
  )
}
